import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../api';
import './AvatarGallery.css';
import { useToast } from '../UI/ToastContext';

const AvatarGallery = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [imageCache, setImageCache] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [styles, setStyles] = useState({}); // To store style information
  
  // Reference to track image loading attempts
  const loadAttempts = useRef({});
  
  // Hook to use toasts
  const { showError } = useToast();
  
  // Use useCallback to avoid recreating these functions on each render
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/aivatar/user-tasks');
      if (response.data && response.data.tasks) {
        setTasks(response.data.tasks);
      }
      setError(null);
    } catch (err) {
      console.error('Error loading avatars:', err);
      setError('Unable to load your avatars. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch style information from the API
  const fetchStyles = useCallback(async () => {
    try {
      const response = await api.get('/aivatar/styles');
      if (response.data && response.data.success && response.data.styles) {
        // Create a map of styles for easy access by ID
        const stylesMap = {};
        response.data.styles.forEach(style => {
          stylesMap[style.id] = style;
        });
        setStyles(stylesMap);
      }
    } catch (err) {
      console.error('Error loading styles:', err);
    }
  }, []);

  // Load result image for a completed task
  const loadResultImage = useCallback(async (task) => {
    if (!task.result_path || 
        imageCache[task.result_path] || 
        loadingImages[task.result_path] ||
        (loadAttempts.current[task.result_path] && loadAttempts.current[task.result_path] >= 2)) {
      return; // Image is already cached, being loaded, or has failed too many times
    }

    // Increment attempt counter
    loadAttempts.current[task.result_path] = (loadAttempts.current[task.result_path] || 0) + 1;

    try {
      setLoadingImages(prev => ({
        ...prev,
        [task.result_path]: true
      }));

      // Extract filename from path
      const pathParts = task.result_path.split('/');
      const filename = pathParts[pathParts.length - 1];
      
      // Use the new route that returns base64 data
      const response = await api.get(`/aivatar/user-file/data/results/${filename}`, {
        responseType: 'json'
      });
      
      if (response.data && response.data.success && response.data.data) {
        // Create URL from base64 data
        const imageUrl = `data:${response.data.content_type};base64,${response.data.data}`;
        
        // Store image URL in cache
        setImageCache(prev => ({
          ...prev,
          [task.result_path]: imageUrl
        }));

        // Clear any errors
        setImageErrors(prev => {
          const newErrors = {...prev};
          delete newErrors[task.result_path];
          return newErrors;
        });
      }
      
    } catch (err) {
      console.error(`Error loading image for task ${task.id}:`, err);
      // Only set an error if the task is complete
      if (task.status === 'DONE') {
        setImageErrors(prev => ({
          ...prev,
          [task.result_path]: "Unable to load image"
        }));
      }
    } finally {
      setLoadingImages(prev => ({
        ...prev,
        [task.result_path]: false
      }));
    }
  }, [imageCache, loadingImages]);

  // Load reference image for a task
  const loadReferenceImage = useCallback(async (task) => {
    if (!task.reference_path || 
        imageCache[task.reference_path] || 
        loadingImages[task.reference_path] ||
        (loadAttempts.current[task.reference_path] && loadAttempts.current[task.reference_path] >= 2)) {
      return; // Image is already cached, being loaded, or has failed too many times
    }

    // Increment attempt counter
    loadAttempts.current[task.reference_path] = (loadAttempts.current[task.reference_path] || 0) + 1;

    try {
      setLoadingImages(prev => ({
        ...prev,
        [task.reference_path]: true
      }));

      // Extract filename from path
      const pathParts = task.reference_path.split('/');
      const filename = pathParts[pathParts.length - 1];
      
      // Determine user ID from path
      const userIdMatch = task.reference_path.match(/user_(\d+)/);
      const userId = userIdMatch ? userIdMatch[1] : '';
      
      // Use photos route to get the reference image
      const response = await api.get(`/photos/data/${filename}`, {
        responseType: 'json'
      });
      
      if (response.data && response.data.success && response.data.data) {
        // Create URL from base64 data
        const imageUrl = `data:${response.data.content_type};base64,${response.data.data}`;
        
        // Store image URL in cache
        setImageCache(prev => ({
          ...prev,
          [task.reference_path]: imageUrl
        }));

        // Clear any errors
        setImageErrors(prev => {
          const newErrors = {...prev};
          delete newErrors[task.reference_path];
          return newErrors;
        });
      }
      
    } catch (err) {
      console.error(`Error loading reference image for task ${task.id}:`, err);
      setImageErrors(prev => ({
        ...prev,
        [task.reference_path]: "Unable to load image"
      }));
    } finally {
      setLoadingImages(prev => ({
        ...prev,
        [task.reference_path]: false
      }));
    }
  }, [imageCache, loadingImages]);
  
  // Load images once tasks are loaded
  useEffect(() => {
    if (tasks.length > 0) {
      // For each completed task, load the result image
      tasks.forEach(task => {
        if (task.status === 'DONE' && task.result_path) {
          loadResultImage(task);
        }
        // For all tasks, load the reference image
        if (task.reference_path) {
          loadReferenceImage(task);
        }
      });
    }
  }, [tasks, loadResultImage, loadReferenceImage]);

  // Load styles on start
  useEffect(() => {
    fetchStyles();
  }, [fetchStyles]);

  // Effect to load tasks and set up refresh interval
  useEffect(() => {
    // Load tasks initially
    fetchTasks();
    
    // Refresh in-progress tasks every 15 seconds
    let interval = null;
    
    const checkForActiveTasks = () => {
      if (tasks.some(task => task.status === 'TODO' || task.status === 'IN_PROGRESS')) {
        return true;
      }
      return false;
    };
    
    const startPolling = () => {
      if (interval) clearInterval(interval);
      interval = setInterval(() => {
        if (checkForActiveTasks()) {
          fetchTasks();
        } else {
          clearInterval(interval);
        }
      }, 15000);
    };
    
    if (tasks.length > 0 && checkForActiveTasks()) {
      startPolling();
    }
    
    // Clean up interval when component unmounts
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchTasks, refreshCounter]); // Only depends on fetchTasks and refreshCounter

  // Get style name from ID
  const getStyleName = (styleId) => {
    if (styles[styleId]) {
      return styles[styleId].name;
    }
    return `Style ${styleId}`;
  };

  const handleDownload = async (task, e) => {
    e.stopPropagation(); // Prevent modal opening
    
    if (!task.result_path) return;

    try {
      // Show loading state for download button
      setLoadingImages(prev => ({
        ...prev,
        [`download_${task.id}`]: true
      }));
      
      // Extract filename from path
      const pathParts = task.result_path.split('/');
      const filename = pathParts[pathParts.length - 1];
      
      console.log(`Attempting to download: ${filename}`);
      console.log(`Complete path: ${task.result_path}`);
      
      // Option 1: Try to diagnose first
      try {
        const diagResponse = await api.get('/aivatar/diagnose/directories');
        console.log("Diagnostic result:", diagResponse.data);
      } catch (diagError) {
        console.error("Error during diagnosis:", diagError);
      }
      
      // Option 2: Try base64 version first
      try {
        console.log("Attempting with base64 API");
        const b64Response = await api.get(`/aivatar/user-file/data/results/${filename}`);
        
        if (b64Response.data && b64Response.data.success && b64Response.data.data) {
          console.log("Successfully retrieved base64 data, creating blob");
          const binary = atob(b64Response.data.data);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          
          const blob = new Blob([bytes], { type: b64Response.data.content_type });
          const url = window.URL.createObjectURL(blob);
          
          // Create temporary <a> element to trigger download
          const link = document.createElement('a');
          link.href = url;
          link.style.display = 'none';
          
          // Determine file extension
          const extension = filename.substring(filename.lastIndexOf('.'));
          
          // Set download filename (prefer simpler name)
          const displayName = filename.includes('_') 
            ? filename.split('_')[0] + extension 
            : filename;
          
          link.setAttribute('download', displayName);
          document.body.appendChild(link);
          
          // Trigger download
          link.click();
          
          // Cleanup
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
          }, 100);
          
          return; // Exit if base64 method worked
        }
      } catch (b64Error) {
        console.error("Failed to retrieve base64 data:", b64Error);
      }
      
      // Option 3: Try standard blob method
      console.log("Attempting with blob API");
      const response = await api.get(`/aivatar/user-file/results/${filename}`, {
        responseType: 'blob' // Important: specify blob response type
      });
      
      // Create URL from received blob
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      
      console.log(`Blob created successfully, type: ${response.headers['content-type']}, size: ${response.data.size}`);
      
      // Create temporary <a> element to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.style.display = 'none';
      
      // Determine file extension
      const extension = filename.substring(filename.lastIndexOf('.'));
      
      // Set download filename (prefer simpler name)
      const displayName = filename.includes('_') 
        ? filename.split('_')[0] + extension 
        : filename;
      
      console.log(`Downloading file with name: ${displayName}`);
      
      link.setAttribute('download', displayName);
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }, 100);
      
    } catch (err) {
      console.error("Error downloading image:", err);
      console.error("Error message:", err.message);
      if (err.response) {
        console.error("Response details:", err.response.data);
        console.error("Status:", err.response.status);
      }
      // Replace French message with English message
      showError("An error occurred while downloading the image. Check console for details.");
    } finally {
      // Remove loading state
      setLoadingImages(prev => {
        const newLoadingState = { ...prev };
        delete newLoadingState[`download_${task.id}`];
        return newLoadingState;
      });
    }
  };

  const openModal = (task) => {
    if (task.status === 'DONE' && task.result_path) {
      setSelectedAvatar(task);
      setShowModal(true);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAvatar(null);
  };

  const refreshGallery = () => {
    // Reset cache and states
    setImageCache({});
    setLoadingImages({});
    setImageErrors({});
    loadAttempts.current = {}; // Reset attempts
    setRefreshCounter(prev => prev + 1); // Increment to trigger a new load
    fetchTasks(); // Load tasks immediately
    fetchStyles(); // Also reload styles
  };

  const renderTaskStatus = (task) => {
    switch (task.status) {
      case 'TODO':
        return (
          <div className="avatar-status pending">
            <div className="status-indicator"></div>
            <span>Pending</span>
          </div>
        );
      case 'IN_PROGRESS':
        return (
          <div className="avatar-status progress">
            <div className="loader"></div>
            <span>{task.progress || 0}%</span>
          </div>
        );
      case 'ERROR':
        return (
          <div className="avatar-status error">
            <div className="status-indicator"></div>
            <span>Error</span>
          </div>
        );
      default:
        return null;
    }
  };

  const getTaskDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const groupTasksByDate = () => {
    const grouped = {};

    tasks.forEach(task => {
      // Convert timestamp to date (day only)
      const date = new Date(task.created_at * 1000);
      const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      
      // Create group if it doesn't exist
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          dateLabel: date.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }),
          tasks: []
        };
      }
      
      // Add task to group
      grouped[dateKey].tasks.push(task);
    });

    // Sort groups by date (newest first)
    return Object.values(grouped).sort((a, b) => 
      new Date(b.tasks[0].created_at * 1000) - new Date(a.tasks[0].created_at * 1000)
    );
  };

  if (loading && tasks.length === 0) {
    return (
      <div className="avatar-gallery-container">
        <div className="gallery-loading">
          <div className="loader"></div>
          <p>Loading your avatars...</p>
        </div>
      </div>
    );
  }

  const groupedTasks = groupTasksByDate();

  return (
    <div className="avatar-gallery-container">
      <div className="gallery-header">
        <h1>My Avatars</h1>
        <button 
          className="refresh-button" 
          onClick={refreshGallery}
          title="Refresh gallery"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 12C2 6.48 6.48 2 12 2C17.52 2 22 6.48 22 12C22 17.52 17.52 22 12 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M8 16H2V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 10L16 10L16 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      
      {error && <div className="gallery-error">{error}</div>}
      
      {tasks.length === 0 ? (
        <div className="no-avatars">
          <p>You haven't created any avatars yet.</p>
          <p>Go to "Create Avatar" to generate one!</p>
        </div>
      ) : (
        <div className="avatar-groups">
          {groupedTasks.map((group, groupIndex) => (
            <div key={groupIndex} className="avatar-group">
              <h2 className="date-header">{group.dateLabel}</h2>
              <div className="avatars-grid">
                {group.tasks.map((task) => (
                  <div 
                    key={task.id} 
                    className={`avatar-card ${task.status.toLowerCase()}`}
                    onClick={() => openModal(task)}
                  >
                    <div className="avatar-image-container">
                      {task.status === 'DONE' ? (
                        <>
                          {loadingImages[task.result_path] && (
                            <div className="image-loading">
                              <div className="loader"></div>
                            </div>
                          )}
                          <img 
                            src={imageCache[task.result_path] || '/placeholder-image.svg'}
                            alt={`Avatar ${task.id}`}
                            className="avatar-image"
                            style={{opacity: loadingImages[task.result_path] ? 0 : 1}}
                            onError={(e) => {
                              // Only set an error if the task is complete
                              if (task.status === 'DONE') {
                                setImageErrors(prev => ({
                                  ...prev,
                                  [task.result_path]: "Unable to load image"
                                }));
                              }
                              e.target.src = '/placeholder-error.png';
                            }}
                          />
                          {imageErrors[task.result_path] && task.status === 'DONE' && (
                            <div className="image-error-overlay">
                              <i className="fas fa-exclamation-triangle"></i>
                              <span>{imageErrors[task.result_path]}</span>
                            </div>
                          )}
                          <button 
                            className="download-button" 
                            onClick={(e) => handleDownload(task, e)}
                            title="Download avatar"
                            disabled={loadingImages[task.result_path] || imageErrors[task.result_path]}
                          >
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 16L12 8M12 16L15 13M12 16L9 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M3 15V16C3 18.2091 4.79086 20 7 20H17C19.2091 20 21 18.2091 21 16V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </>
                      ) : (
                        <>
                          {loadingImages[task.reference_path] && (
                            <div className="image-loading">
                              <div className="loader"></div>
                            </div>
                          )}
                          <img 
                            src={imageCache[task.reference_path] || '/placeholder-image.svg'}
                            alt={`Reference ${task.id}`}
                            className="reference-image"
                            style={{opacity: loadingImages[task.reference_path] ? 0.7 : 0.7}}
                            onError={(e) => {
                              e.target.src = '/placeholder-error.png';
                            }}
                          />
                          {renderTaskStatus(task)}
                        </>
                      )}
                    </div>
                    <div className="avatar-info">
                      <div className="avatar-time">{getTaskDate(task.updated_at || task.created_at)}</div>
                      <div className="avatar-style">Style: {getStyleName(task.style_id)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal to view avatar in full size */}
      {showModal && selectedAvatar && (
        <div className="avatar-modal-overlay" onClick={closeModal}>
          <div className="avatar-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Generated Avatar</h2>
              <button className="close-button" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              {imageCache[selectedAvatar.result_path] ? (
                <img 
                  src={imageCache[selectedAvatar.result_path]}
                  alt={`Avatar ${selectedAvatar.id}`} 
                  className="modal-avatar-image"
                  onError={(e) => {
                    setImageErrors(prev => ({
                      ...prev,
                      [selectedAvatar.result_path]: "Unable to load image"
                    }));
                    e.target.src = '/placeholder-error.png';
                  }}
                />
              ) : (
                <div className="modal-image-loading">
                  <div className="loader"></div>
                  <p>Loading image...</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <div className="avatar-details">
                <p><strong>Style:</strong> {getStyleName(selectedAvatar.style_id)}</p>
                <p><strong>Created:</strong> {getTaskDate(selectedAvatar.created_at)}</p>
              </div>
              <button 
                className="btn btn-primary" 
                onClick={(e) => handleDownload(selectedAvatar, e)}
                disabled={!imageCache[selectedAvatar.result_path]}
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvatarGallery; 